import React, { useState, useRef, useCallback } from 'react';

export default function PdfTools() {
  const [splitMode, setSplitMode] = useState(false);
  const [files, setFiles] = useState([]);
  const [addToc, setAddToc] = useState(false);
  const [merging, setMerging] = useState(false);
  const [splitFile, setSplitFile] = useState(null);
  const [splitRanges, setSplitRanges] = useState('');
  const [splitting, setSplitting] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = useCallback((newFiles) => {
    const items = Array.from(newFiles).filter(f => f.type === 'application/pdf').map(f => ({ id: crypto.randomUUID(), file: f, name: f.name }));
    setFiles(prev => [...prev, ...items]);
  }, []);

  const handleMerge = async () => {
    if (files.length < 2) return;
    setMerging(true); setError(null);
    const fd = new FormData();
    files.forEach(f => fd.append('files', f.file));
    fd.append('add_toc', addToc);
    try {
      const r = await fetch('/api/merge', { method: 'POST', body: fd });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'merged.pdf'; a.click();
    } catch (e) { setError(e.message); }
    setMerging(false);
  };

  const handleSplit = async () => {
    if (!splitFile || !splitRanges.trim()) return;
    setSplitting(true); setError(null);
    const fd = new FormData();
    fd.append('file', splitFile);
    fd.append('ranges', splitRanges);
    try {
      const r = await fetch('/api/split', { method: 'POST', body: fd });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'split.pdf'; a.click();
    } catch (e) { setError(e.message); }
    setSplitting(false);
  };

  return (
    <div style={{ padding: 'var(--gap-lg)', maxWidth: 600 }}>
      <h2 style={{ color: 'var(--text-primary)', marginBottom: 'var(--gap-lg)', fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>PDF Tools</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--gap-md)' }}>
        {[false, true].map(m => (
          <button key={String(m)} onClick={() => setSplitMode(m)} style={{ background: splitMode === m ? 'rgba(187,154,247,0.2)' : 'var(--bg-tertiary)', border: `1px solid ${splitMode === m ? '#bb9af7' : 'var(--border)'}`, borderRadius: 'var(--border-radius)', padding: '6px 14px', color: splitMode === m ? '#bb9af7' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
            {m ? 'Trennen' : 'Zusammenfuehren'}
          </button>
        ))}
      </div>

      {!splitMode ? (
        <>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            style={{ border: `2px dashed ${dragOver ? '#bb9af7' : 'var(--border)'}`, borderRadius: 'var(--border-radius-lg)', padding: 32, textAlign: 'center', cursor: 'pointer', background: 'var(--bg-secondary)', marginBottom: 'var(--gap-md)' }}
          >
            <span style={{ color: 'var(--text-muted)' }}>PDFs hierher ziehen</span>
            <input ref={inputRef} type="file" multiple accept=".pdf" hidden onChange={e => addFiles(e.target.files)} />
          </div>
          {files.map((f, i) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '8px 12px', marginBottom: 4, color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)' }}>
              <span>{i + 1}. {f.name}</span>
              <button onClick={() => setFiles(prev => prev.filter(x => x.id !== f.id))} style={{ background: 'none', border: 'none', color: '#f7768e', cursor: 'pointer', fontSize: 16 }}>{'\u2715'}</button>
            </div>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, marginBottom: 12, color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
            <input type="checkbox" checked={addToc} onChange={e => setAddToc(e.target.checked)} /> Inhaltsverzeichnis
          </label>
          <button onClick={handleMerge} disabled={files.length < 2 || merging} style={{ background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '10px 24px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer', opacity: (files.length < 2 || merging) ? 0.5 : 1 }}>
            {merging ? 'Merge...' : 'Zusammenfuehren'}
          </button>
        </>
      ) : (
        <>
          <div onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.accept = '.pdf'; i.onchange = e => setSplitFile(e.target.files[0]); i.click(); }}
            style={{ border: '2px dashed var(--border)', borderRadius: 'var(--border-radius-lg)', padding: 32, textAlign: 'center', cursor: 'pointer', background: splitFile ? 'rgba(187,154,247,0.05)' : 'var(--bg-secondary)', marginBottom: 'var(--gap-md)' }}
          >
            {splitFile ? <span style={{ color: 'var(--text-primary)' }}>{splitFile.name}</span> : <span style={{ color: 'var(--text-muted)' }}>PDF waehlen</span>}
          </div>
          <input value={splitRanges} onChange={e => setSplitRanges(e.target.value)} placeholder="z.B. 1-3, 4, 5-10" style={{ width: '100%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 'var(--font-size-base)', outline: 'none', marginBottom: 12 }} />
          <button onClick={handleSplit} disabled={!splitFile || !splitRanges.trim() || splitting} style={{ background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '10px 24px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer', opacity: (!splitFile || splitting) ? 0.5 : 1 }}>
            {splitting ? 'Trenne...' : 'Trennen'}
          </button>
        </>
      )}
      {error && <div style={{ color: '#f7768e', marginTop: 12, fontSize: 'var(--font-size-sm)' }}>{error}</div>}
    </div>
  );
}
